import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'

import { useMinimizedModalsContext } from '../../../../../hooks'
import { getItemsIds } from '../../../../../utils/common'
import { MINIMIZED_MODAL_SCOPE } from '../../../../Modal'
import { useRequestProfileContext } from '../../../RequestProfile.hooks'
import { getRequestProfileTabUrl } from '../../RequestProfileTabs.utils'

export function useRequestProfileTabsActiveTab({ preparedTabs, requestData }) {
  const { isPreview } = useRequestProfileContext()

  const { confirmBeforeAction } = useMinimizedModalsContext()

  const navigate = useNavigate()

  const location = useLocation()

  const { outboundUuid, requestId, tab: tabFromUrl } = useParams()

  const [localTabId, setLocalTabId] = useState(null)

  const isRequestLoaded = Boolean(requestData)

  const availableTabIds = getItemsIds(preparedTabs)

  const defaultTabId = preparedTabs[0]?.id

  // Use the current tab only when it's one this user can access; otherwise fall
  // back to their first available tab.
  const selectedTabId = isPreview ? localTabId : tabFromUrl

  const isSelectedTabAvailable =
    Boolean(selectedTabId) && availableTabIds.includes(selectedTabId)

  const activeTabId = isSelectedTabAvailable ? selectedTabId : defaultTabId

  // Once the request (and its permissions) have loaded, reflect the resolved
  // active tab in the URL whenever it's missing or names a tab the user can't
  // access — so the active tab is always shareable and never points at a tab
  // the user lacks. Preserve the current search/hash so deep-link params
  // (e.g. sub-tab) aren't dropped. Skipped in preview (URL is never touched).
  useEffect(() => {
    if (
      !isPreview &&
      isRequestLoaded &&
      activeTabId &&
      tabFromUrl !== activeTabId
    ) {
      navigate(
        {
          hash: location.hash,
          pathname: getRequestProfileTabUrl({
            outboundUuid,
            requestId,
            tab: activeTabId,
          }),
          search: location.search,
        },
        { replace: true }
      )
    }
  }, [
    activeTabId,
    isPreview,
    isRequestLoaded,
    location.hash,
    location.search,
    outboundUuid,
    requestId,
    tabFromUrl,
  ])

  function setActiveTab(tabId) {
    if (isPreview) {
      setLocalTabId(tabId)

      return
    }

    navigate(getRequestProfileTabUrl({ outboundUuid, requestId, tab: tabId }), {
      replace: true,
    })
  }

  // Only hosts living inside the tab content (reply forms in the emails-tab
  // preview or thread modal, a tab's create-email popup) track dirty state
  // that a tab switch would silently discard; `REQUEST_PROFILE` hosts sit at
  // the profile root and survive it.
  function handleSetActiveTab(tabId) {
    confirmBeforeAction(() => setActiveTab(tabId), {
      scope: [
        MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE,
        MINIMIZED_MODAL_SCOPE.REQUEST_PROFILE_EMAIL_THREAD,
        MINIMIZED_MODAL_SCOPE.REQUEST_PROFILE_TAB,
      ],
    })
  }

  return {
    activeTabId,
    handleSetActiveTab,
  }
}
